import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit2, Archive, EyeOff, Eye, Sparkles, X, Loader2, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';

const FEATURE_LABELS = {
  search_enabled: 'Buscador',
  ai_enabled: 'IA (rerank búsquedas)',
  chat_enabled: 'Chat (sin IA)',
  chat_ai_enabled: 'Chat con IA',
  whatsapp_enabled: 'WhatsApp',
  customer_lookup_enabled: 'Lookup pedidos',
  reviews_enabled: 'Módulo de Opiniones',
};

const EMPTY_FORM = {
  plan_key: '',
  name: '',
  price: 0,
  description: '',
  is_hidden: true,
  features: {
    search_enabled: false,
    ai_enabled: false,
    chat_enabled: false,
    chat_ai_enabled: false,
    whatsapp_enabled: false,
    customer_lookup_enabled: false,
    reviews_enabled: false,
    ai_searches_limit: 0,
    chat_messages_limit: 0,
    chat_conversations_limit: 0,
    agents_limit: 1,
    search_quota_classic_daily: 0,
    search_quota_classic_monthly: 0,
    search_quota_ai_daily: 0,
    search_quota_ai_monthly: 0,
    search_quota_distinct_products_daily: 0,
  },
};

function PlanFormModal({ open, mode, initial, onClose, onSaved }) {
  // mode: 'create' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setForm({
        plan_key: initial.plan_key,
        name: initial.name || '',
        price: Number(initial.price ?? 0),
        description: initial.description || '',
        is_hidden: !!initial.is_hidden,
        features: {
          search_enabled: !!initial.features?.search_enabled,
          ai_enabled: !!initial.features?.ai_enabled,
          chat_enabled: !!initial.features?.chat_enabled,
          chat_ai_enabled: !!initial.features?.chat_ai_enabled,
          whatsapp_enabled: !!initial.features?.whatsapp_enabled,
          customer_lookup_enabled: !!initial.features?.customer_lookup_enabled,
          reviews_enabled: !!initial.features?.reviews_enabled,
          ai_searches_limit: Number(initial.features?.ai_searches_limit ?? 0),
          chat_messages_limit: Number(initial.features?.chat_messages_limit ?? 0),
          chat_conversations_limit: Number(initial.features?.chat_conversations_limit ?? 0),
          agents_limit: Number(initial.features?.agents_limit ?? 1),
          search_quota_classic_daily: Number(initial.features?.search_quota_classic_daily ?? 0),
          search_quota_classic_monthly: Number(initial.features?.search_quota_classic_monthly ?? 0),
          search_quota_ai_daily: Number(initial.features?.search_quota_ai_daily ?? 0),
          search_quota_ai_monthly: Number(initial.features?.search_quota_ai_monthly ?? 0),
          search_quota_distinct_products_daily: Number(initial.features?.search_quota_distinct_products_daily ?? 0),
        },
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, mode, initial]);

  if (!open) return null;

  const isEdit = mode === 'edit';

  const setFeat = (key, value) => setForm(f => ({ ...f, features: { ...f.features, [key]: value } }));

  const submit = async () => {
    if (!isEdit) {
      if (!/^[a-z0-9][a-z0-9_-]{1,40}$/.test(form.plan_key)) {
        toast.error('plan_key inválido (a-z, 0-9, _ y -)');
        return;
      }
      if (!form.name.trim()) {
        toast.error('Nombre obligatorio');
        return;
      }
    }
    if (form.price < 0) {
      toast.error('Precio inválido');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          name: form.name,
          price: form.price,
          description: form.description,
          is_hidden: form.is_hidden,
          features: form.features,
        };
        await api.put(`/api/admin/plans/${form.plan_key}`, payload);
        toast.success('Plan actualizado');
      } else {
        await api.post('/api/admin/plans', {
          plan_key: form.plan_key,
          name: form.name,
          price: form.price,
          description: form.description,
          is_hidden: form.is_hidden,
          features: form.features,
        });
        toast.success('Plan creado y sincronizado con Stripe');
      }
      onSaved();
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error guardando el plan'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="plan-form-modal"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold text-lg">
              {isEdit ? 'Editar plan' : 'Crear plan personalizado'}
            </h2>
            <p className="text-gray-500 text-xs mt-0.5">
              {isEdit ? form.plan_key : 'Se sincronizará automáticamente con Stripe'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-xs">plan_key (identificador único)</Label>
              <Input
                data-testid="plan-key-input"
                value={form.plan_key}
                onChange={e => setForm(f => ({ ...f, plan_key: e.target.value.toLowerCase() }))}
                placeholder="enterprise_acme"
                className="bg-gray-800 border-gray-700 text-white"
              />
              <p className="text-gray-600 text-xs">Solo a-z, 0-9, _ y -</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-xs">Nombre</Label>
              <Input
                data-testid="plan-name-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="ACME Enterprise"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-xs">Precio (€ / mes)</Label>
              <Input
                data-testid="plan-price-input"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: Number(e.target.value) }))}
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Descripción</Label>
            <textarea
              data-testid="plan-description-input"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white text-sm placeholder-gray-500 outline-none focus:border-indigo-500"
              placeholder="Plan a medida para..."
            />
          </div>

          {/* Features */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-gray-200 text-sm font-medium">Módulos incluidos</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {Object.entries(FEATURE_LABELS).map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg hover:border-indigo-600/50 cursor-pointer"
                >
                  <span className="text-gray-300 text-sm">{label}</span>
                  <Switch
                    data-testid={`feature-${key}-switch`}
                    checked={!!form.features[key]}
                    onCheckedChange={(v) => setFeat(key, v)}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-xs">Límite búsquedas IA / mes</Label>
              <Input
                data-testid="ai-limit-input"
                type="number"
                min="0"
                value={form.features.ai_searches_limit}
                onChange={e => setFeat('ai_searches_limit', Number(e.target.value))}
                className="bg-gray-800 border-gray-700 text-white"
              />
              <p className="text-gray-600 text-xs">0 = ilimitado</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-300 text-xs">Límite mensajes chat / mes</Label>
              <Input
                data-testid="chat-limit-input"
                type="number"
                min="0"
                value={form.features.chat_messages_limit}
                onChange={e => setFeat('chat_messages_limit', Number(e.target.value))}
                className="bg-gray-800 border-gray-700 text-white"
              />
              <p className="text-gray-600 text-xs">0 = ilimitado</p>
            </div>
          </div>

          {/* Límite de conversaciones IA */}
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Conversaciones con IA / mes</Label>
            <Input
              data-testid="conv-limit-input"
              type="number"
              min="0"
              value={form.features.chat_conversations_limit}
              onChange={e => setFeat('chat_conversations_limit', Number(e.target.value))}
              className="bg-gray-800 border-gray-700 text-white"
            />
            <p className="text-gray-600 text-xs">0 = ilimitado. Al superar el límite el bot no responde y la conversación pasa directamente a un agente.</p>
          </div>

          {/* Agentes incluidos de base */}
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Agentes incluidos en el plan</Label>
            <Input
              data-testid="agents-limit-input"
              type="number"
              min="0"
              value={form.features.agents_limit}
              onChange={e => setFeat('agents_limit', Number(e.target.value))}
              className="bg-gray-800 border-gray-700 text-white"
            />
            <p className="text-gray-600 text-xs">
              Número de miembros que la organización puede tener en su equipo. 0 = ilimitado.
              Se suma con los agentes extra contratados como add-on (<code className="text-amber-400/70">extra_agents_count</code>).
            </p>
          </div>

          {/* Fase 2 — cuotas de búsqueda (anti-scraping / control de coste) */}
          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs font-semibold">Cuotas de búsqueda (clásica)</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Diaria</Label>
                <Input
                  data-testid="quota-classic-daily-input"
                  type="number"
                  min="0"
                  value={form.features.search_quota_classic_daily}
                  onChange={e => setFeat('search_quota_classic_daily', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Mensual</Label>
                <Input
                  data-testid="quota-classic-monthly-input"
                  type="number"
                  min="0"
                  value={form.features.search_quota_classic_monthly}
                  onChange={e => setFeat('search_quota_classic_monthly', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <p className="text-gray-600 text-xs">0 = ilimitado. Cuenta toda búsqueda con texto (clásica + base de IA).</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs font-semibold">Cuotas de búsqueda (IA / rerank)</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Diaria</Label>
                <Input
                  data-testid="quota-ai-daily-input"
                  type="number"
                  min="0"
                  value={form.features.search_quota_ai_daily}
                  onChange={e => setFeat('search_quota_ai_daily', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Mensual</Label>
                <Input
                  data-testid="quota-ai-monthly-input"
                  type="number"
                  min="0"
                  value={form.features.search_quota_ai_monthly}
                  onChange={e => setFeat('search_quota_ai_monthly', Number(e.target.value))}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <p className="text-gray-600 text-xs">0 = ilimitado. Solo cuenta las búsquedas servidas con reranking de Claude.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-gray-300 text-xs">Productos distintos devueltos / día</Label>
            <Input
              data-testid="quota-distinct-products-input"
              type="number"
              min="0"
              value={form.features.search_quota_distinct_products_daily}
              onChange={e => setFeat('search_quota_distinct_products_daily', Number(e.target.value))}
              className="bg-gray-800 border-gray-700 text-white"
            />
            <p className="text-gray-600 text-xs">0 = ilimitado. Detecta scraping de catálogo repartido en muchas búsquedas distintas.</p>
          </div>

          {/* Hidden toggle */}
          <label className="flex items-center justify-between px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg cursor-pointer hover:border-indigo-600/50">
            <div>
              <p className="text-gray-200 text-sm font-medium">Plan oculto</p>
              <p className="text-gray-500 text-xs mt-0.5">No aparece en el listado público de planes en /billing</p>
            </div>
            <Switch
              data-testid="plan-hidden-switch"
              checked={form.is_hidden}
              onCheckedChange={(v) => setForm(f => ({ ...f, is_hidden: v }))}
            />
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-6 py-4 flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose} disabled={saving} className="border-gray-700 text-gray-300">
            Cancelar
          </Button>
          <Button
            data-testid="plan-form-save-btn"
            onClick={submit}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            {isEdit ? 'Guardar cambios' : 'Crear plan'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CustomPlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/admin/plans?include_hidden=true');
      setPlans(r.data.plans || []);
    } catch (e) {
      toast.error('Error cargando planes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingPlan(null); setFormOpen(true); };
  const openEdit = (plan) => { setEditingPlan(plan); setFormOpen(true); };

  const toggleHidden = async (plan) => {
    try {
      await api.put(`/api/admin/plans/${plan.plan_key}`, { is_hidden: !plan.is_hidden });
      toast.success(plan.is_hidden ? 'Plan visible en catálogo' : 'Plan oculto');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error cambiando visibilidad');
    }
  };

  const archivePlan = async (plan) => {
    if (!window.confirm(`¿Eliminar el plan "${plan.name}" definitivamente? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/api/admin/plans/${plan.plan_key}`);
      toast.success('Plan archivado');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se puede archivar');
    }
  };

  const [syncingPlan, setSyncingPlan] = useState(null);
  const syncStripe = async (plan) => {
    if (!window.confirm(`Sincronizar "${plan.name}" con Stripe? Se creará (o actualizará) el Producto + Precio.`)) return;
    setSyncingPlan(plan.plan_key);
    try {
      await api.post(`/api/admin/plans/${plan.plan_key}/sync-stripe`);
      toast.success(`Plan "${plan.name}" sincronizado con Stripe`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error sincronizando con Stripe');
    } finally {
      setSyncingPlan(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">Planes</h2>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona planes estándar y crea planes personalizados ocultos</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-gray-400 hover:text-white text-sm flex items-center gap-1">
            <RefreshCw size={14} /> Actualizar
          </button>
          <Button
            data-testid="create-custom-plan-btn"
            onClick={openCreate}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus size={14} className="mr-1.5" /> Crear plan custom
          </Button>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900/50">
              <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Plan</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Precio</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Módulos</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Stripe</th>
              <th className="text-left px-4 py-3 text-gray-400 font-medium text-xs uppercase tracking-wide">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-4 bg-gray-800 rounded animate-pulse w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : plans.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-500">Sin planes</td>
              </tr>
            ) : plans.map(plan => {
              const features = plan.features || {};
              const activeFeatures = Object.entries(FEATURE_LABELS)
                .filter(([k]) => features[k])
                .map(([, label]) => label);
              return (
                <tr
                  key={plan.plan_key}
                  data-testid={`plan-row-${plan.plan_key}`}
                  className="border-b border-gray-800/50 hover:bg-gray-800/20"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{plan.name}</p>
                      {plan.is_custom && (
                        <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px]">
                          <Sparkles size={9} className="mr-0.5" /> Custom
                        </Badge>
                      )}
                      {plan.is_hidden && (
                        <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px]">
                          <EyeOff size={9} className="mr-0.5" /> Oculto
                        </Badge>
                      )}
                      {plan.is_active === false && (
                        <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 text-[10px]">
                          Archivado
                        </Badge>
                      )}
                    </div>
                    <p className="text-gray-600 text-xs font-mono mt-0.5">{plan.plan_key}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-200">{Number(plan.price || 0).toFixed(2)}€</p>
                    <p className="text-gray-600 text-xs">/{plan.interval === 'one_time' ? 'único' : 'mes'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {activeFeatures.length === 0 ? (
                        <span className="text-gray-600 text-xs">—</span>
                      ) : activeFeatures.map(f => (
                        <span key={f} className="bg-gray-800 text-gray-300 text-[10px] px-1.5 py-0.5 rounded">{f}</span>
                      ))}
                    </div>
                    {(features.ai_searches_limit > 0 || features.chat_messages_limit > 0 || features.agents_limit > 0) && (
                      <p className="text-gray-600 text-[10px] mt-1">
                        {features.ai_searches_limit > 0 && `IA: ${features.ai_searches_limit.toLocaleString()}/mes`}
                        {features.ai_searches_limit > 0 && features.chat_messages_limit > 0 && ' · '}
                        {features.chat_messages_limit > 0 && `Chat: ${features.chat_messages_limit.toLocaleString()}/mes`}
                        {(features.ai_searches_limit > 0 || features.chat_messages_limit > 0) && features.agents_limit > 0 && ' · '}
                        {features.agents_limit > 0 && `Agentes: ${features.agents_limit}`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {plan.stripe_price_id ? (
                      <p className="text-gray-500 text-xs font-mono truncate max-w-[140px]" title={plan.stripe_price_id}>
                        {plan.stripe_price_id}
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-amber-500 text-xs">no sync</span>
                        <button
                          data-testid={`sync-stripe-${plan.plan_key}-btn`}
                          onClick={() => syncStripe(plan)}
                          disabled={syncingPlan === plan.plan_key}
                          className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 disabled:opacity-50"
                          title="Crear producto y precio en Stripe"
                        >
                          {syncingPlan === plan.plan_key ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                          Sync
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={`edit-plan-${plan.plan_key}-btn`}
                        onClick={() => openEdit(plan)}
                        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                      >
                        <Edit2 size={11} /> Editar
                      </button>
                      <button
                        onClick={() => toggleHidden(plan)}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                        title={plan.is_hidden ? 'Hacer visible' : 'Ocultar'}
                      >
                        {plan.is_hidden ? <Eye size={11} /> : <EyeOff size={11} />}
                        {plan.is_hidden ? 'Mostrar' : 'Ocultar'}
                      </button>
                      {plan.is_active !== false && (
                        <button
                          data-testid={`delete-plan-${plan.plan_key}-btn`}
                          onClick={() => archivePlan(plan)}
                          className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                        >
                          <Archive size={11} /> Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PlanFormModal
        open={formOpen}
        mode={editingPlan ? 'edit' : 'create'}
        initial={editingPlan}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
